
import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import { blogPosts } from '../../mocks/blog';

export default function Blog() {
  const [selectedCategory, setSelectedCategory] = useState('All');

  const categories = ['All', 'Personal Growth', 'Faith & Business', 'Journaling', 'Leadership', 'Purpose', 'Communication'];

  const filteredPosts = blogPosts.filter(post => 
    selectedCategory === 'All' || post.category === selectedCategory
  );

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-slate-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold text-slate-800 mb-6">Author's Journal</h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Insights, reflections, and practical wisdom from my journey as an author and entrepreneur. 
            Discover the creative process behind the books and find inspiration for your own growth.
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-2 justify-center">
            {categories.map(category => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium cursor-pointer whitespace-nowrap ${
                  selectedCategory === category 
                    ? 'bg-slate-800 text-white' 
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Post */}
      {filteredPosts.length > 0 && (
        <section className="py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                <div className="relative h-64 lg:h-auto">
                  <img 
                    src={filteredPosts[0].image}
                    alt={filteredPosts[0].title}
                    className="w-full h-full object-cover object-top"
                  />
                  <span className="absolute top-4 left-4 px-3 py-1 bg-slate-800 text-white text-sm rounded-full">
                    Featured
                  </span>
                </div>
                <div className="p-8 lg:p-12 flex flex-col justify-center">
                  <span className="inline-block px-3 py-1 bg-slate-100 text-slate-600 rounded-full text-sm mb-4 w-fit">
                    {filteredPosts[0].category}
                  </span>
                  <h2 className="text-3xl font-bold text-slate-800 mb-4">{filteredPosts[0].title}</h2>
                  <p className="text-lg text-slate-600 mb-6">{filteredPosts[0].excerpt}</p>
                  <div className="flex items-center text-slate-500 text-sm mb-6">
                    <span>{new Date(filteredPosts[0].date).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}</span>
                    <span className="mx-2">•</span>
                    <span>{filteredPosts[0].readTime}</span>
                  </div>
                  <Link to="/blog/power-of-recording-dreams">
                    <Button>Read Full Article</Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Newsletter Signup */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-slate-800 mb-6">Stay Connected</h2>
          <p className="text-xl text-slate-600 mb-8">
            Get the latest insights, book updates, and exclusive content delivered to your inbox.
          </p>
          <div className="max-w-md mx-auto">
            <div className="flex gap-3">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500 text-sm"
              />
              <a href="mailto:elbertalexander@gmail.com">
                <Button>Subscribe</Button>
              </a>
            </div>
            <p className="text-slate-500 text-sm mt-3">
              No spam, unsubscribe at any time.
            </p>
          </div>
        </div>
      </section>

      {/* Writing Process */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-800 mb-12 text-center">Behind the Scenes</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-slate-100 rounded-full mx-auto mb-4">
                <i className="ri-lightbulb-line text-2xl text-slate-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-3">Inspiration</h3>
              <p className="text-slate-600">
                Ideas come from real-life experiences, reader feedback, and moments of spiritual insight 
                that demand to be shared.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-slate-100 rounded-full mx-auto mb-4">
                <i className="ri-edit-line text-2xl text-slate-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-3">Creation</h3>
              <p className="text-slate-600">
                Each piece is carefully crafted to provide practical value while maintaining authenticity 
                and spiritual depth.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-slate-100 rounded-full mx-auto mb-4">
                <i className="ri-share-line text-2xl text-slate-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-3">Impact</h3>
              <p className="text-slate-600">
                The goal is always transformation - helping readers apply insights to create meaningful 
                change in their lives.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
